import { OnboardingDarkBackground } from "@/features/onboarding/components/onboarding-dark-background";
import { OnboardingTopNav } from "@/features/onboarding/components/onboarding-top-nav";
import { onboardingContentStyle } from "@/features/onboarding/constants/theme";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { safeAreaPadding } from "@/utils/safe-area-classes";
import { onboardingFadeTransition } from "@/utils/transitions/onboarding-fade-transition";
import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import { ReactNode } from "react";
import { useLocation } from "react-router-dom";

const PROGRESS_SEGMENTS = 9;

interface OnboardingStepShellV5Props {
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  /** Bottom CTA area, pinned above the safe-area inset. */
  footer: ReactNode;
  /**
   * Overrides the back target. Needed where the flow steps outside the step router
   * and back again — /diagnostic sits between the name and goal steps.
   */
  previousPath?: string;
  /** Overrides the derived segment count, for screens whose Figma frame specifies one. */
  progressFilled?: number;
}

/**
 * Dark v5 step layout: top nav (back + segmented progress derived from the step
 * router), serif title + subtitle, scrollable content, bottom CTA.
 */
export const OnboardingStepShellV5 = ({
  title,
  subtitle,
  children,
  footer,
  previousPath: previousPathOverride,
  progressFilled: progressFilledOverride,
}: OnboardingStepShellV5Props) => {
  const navigation = useIonRouter();
  const location = useLocation();
  const { info, totalSteps } = useStepRouteInfo();

  const step = info?.step;
  const derivedProgress =
    step && totalSteps
      ? Math.min(
          PROGRESS_SEGMENTS,
          Math.max(1, Math.round((step / totalSteps) * PROGRESS_SEGMENTS)),
        )
      : undefined;
  const progressFilled = progressFilledOverride ?? derivedProgress;

  const previousPath = previousPathOverride ?? info?.previousPath;
  // Hide the back button on the first step: when the back target is missing or
  // resolves to the current route there is nowhere to go back to. This makes the
  // name step back-less while /get-started is disabled, and restores the back
  // button automatically once the intro (a distinct previous path) returns.
  const showBack = Boolean(previousPath) && previousPath !== location.pathname;
  const handleBack = () => {
    if (previousPath) {
      navigation.push(previousPath, "back", "pop", undefined, onboardingFadeTransition);
    } else {
      navigation.goBack(onboardingFadeTransition);
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen style={onboardingContentStyle}>
        <div className="relative flex min-h-full flex-col bg-[#130c2d]">
          <OnboardingDarkBackground />

          <div
            className={cn(
              "relative z-10 flex flex-1 flex-col",
              safeAreaPadding({ top: "16px", bottom: "12px" }),
            )}
          >
            <OnboardingTopNav
              onBack={handleBack}
              progressFilled={progressFilled}
              progressTotal={PROGRESS_SEGMENTS}
              showBack={showBack}
            />

            <div className="px-9 pt-9">
              <h1 className="font-[family-name:var(--font-crimson-text)] text-[35px] leading-[45px] font-semibold tracking-[-1px] text-white">
                {title}
              </h1>
              {subtitle && (
                <p className="pt-3 text-base leading-[normal] font-medium text-white">{subtitle}</p>
              )}
            </div>

            <div className="flex flex-1 flex-col px-9 pt-10 pb-6">{children}</div>

            <div className="mt-auto px-9">{footer}</div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};
